import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import { AppState } from "../../../../core/reducers";
import { AuthNoticeService, AuthService } from "../../../../core/auth/";
import { Subject } from "rxjs";
import { ConfirmPasswordValidator } from "./confirm-password.validator";
import { ConfirmEmailValidator } from "./confirm-email.validator";
import { MatDialog, MatDialogRef, MatPaginator, MatTableDataSource } from "@angular/material";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { MapsAPILoader, MouseEvent } from "@agm/core";
import { FechaNacimientoValidator } from "./fecha-nacimiento.validator";
import { AtLeastOneValidator } from "./at-least-one.validator";
import { environment } from "../../../../../environments/environment";

declare var google: any;

const API_APPLICATIONUSERS_SEARCH_URL = environment.API_URL + "api/users/find";
const API_INTENCIONREVENTA_START_URL = environment.API_URL + "api/IntencionReventas/start";
const API_INTENCIONREVENTA_DRAFT_URL = environment.API_URL + "api/IntencionReventas/draft";
const API_REGISTER_URL = environment.API_URL + "api/IntencionReventas";

@Component({
  selector: "kt-register",
  templateUrl: "./register.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrls: ["./register.component.css"]
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  loading = false;
  errors: any = [];

  private unsubscribe: Subject<any>;
  mapsLatitude = 0;
  mapsLongitude = 0;
  mapsZoom = 2;
  mapsAddress = "";
  geoCoder;
  placesService;
  intencion;
  provincias;
  province;
  partidos = [];
  barrios = [];

  @ViewChild("search")
  public searchElementRef: ElementRef;

  @ViewChild("agmMap")
  public agmMap;

  @ViewChild("calle")
  public calle;

  @ViewChild("numero")
  public numero;

  // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  /**
   * Component constructor
   *
   * @param authNoticeService: AuthNoticeService
   * @param translate: TranslateService
   * @param router: Router
   * @param auth: AuthService
   * @param store: Store<AppState>
   * @param fb: FormBuilder
   * @param cdr
   * @param dialog
   * @param mapsAPILoader
   * @param ngZone
   * @param http
   */
  constructor(
    private authNoticeService: AuthNoticeService,
    private translate: TranslateService,
    private router: Router,
    private auth: AuthService,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    private http: HttpClient
  ) {
    this.unsubscribe = new Subject();
  }

  /*
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    this.initRegisterForm();
    // https://www.freakyjolly.com/angular-7-6-add-google-maps-in-angular-2-plus-applications-using-angular-google-maps-module-agm-core-easily/
    this.initGoogleMaps();
    this.heartbeat();
    this.getProvincias();
    this.getPartidos();
    this.getBarrios();
  }

  ngAfterViewInit() {
    this.agmMap.mapReady.subscribe((nativeMap) => {
      this.placesService = new google.maps.places.PlacesService(nativeMap);
    });
  }

  getProvincias() {
    this.http.get("assets/json/provincias.json").subscribe((value) => {
      this.provincias = value;
    });
  }

  getPartidos() {
    this.http.get("assets/json/partidos.json").subscribe((value) => {
      // @ts-ignore
      this.partidos = value;
    });
  }

  getBarrios() {
    this.http.get("assets/json/barrios.json").subscribe((value) => {
      // @ts-ignore
      this.barrios = value;
    });
  }

  setPlaceByString(string) {
    let request = { query: string, fields: ["formatted_address", "geometry"] };
    if (this.placesService) {
      this.placesService.findPlaceFromQuery(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          let place = results[0];
          this.mapsLatitude = place.geometry.location.lat();
          this.mapsLongitude = place.geometry.location.lng();
          this.getAddress(this.mapsLatitude, this.mapsLongitude, 15);
        }
      });
    }
  }

  provinceChanged($event){
      this.province = $event.value
  }

  /*
   * On destroy
   */
  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.loading = false;
  }

  /**
   * Form initalization
   * Default params, validators
   */
  initRegisterForm() {
    this.registerForm = this.fb.group(
      {
        lastName: ["", Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
        firstName: ["", Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
        documentNumber: [
          "",
          Validators.compose([Validators.required, Validators.minLength(7), Validators.maxLength(100)])
        ],
        fechaNacimiento: ["", Validators.compose([Validators.required])],
        sexo: ["", Validators.compose([Validators.required])],
        nacionalidad: [
          "",
          Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])
        ],
        email: [
          "",
          Validators.compose([
            Validators.required,
            Validators.email,
            Validators.minLength(3),
            // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
            Validators.maxLength(320)
          ])
        ],
        confirmEmail: [
          "",
          Validators.compose([
            Validators.required,
            Validators.email,
            Validators.minLength(3),
            // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
            Validators.maxLength(320)
          ])
        ],
        password: ["", Validators.compose([Validators.required, Validators.minLength(7), Validators.maxLength(100)])],
        confirmPassword: [
          "",
          Validators.compose([Validators.required, Validators.minLength(7), Validators.maxLength(100)])
        ],
        provincia: ["", Validators.compose([Validators.required])],
        partido: ["", Validators.compose([Validators.required])],
        barrio: ["", Validators.compose([Validators.required])],
        calle: ["", Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
        numero: ["", Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
        codPostal: [],
        piso: [],
        departamento: [],
        complemento: [],
        informacionComplementaria: [
          "",
          Validators.compose([
            // Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100)
          ])
        ],
        // latitud: ['', Validators.compose([
        // 	Validators.required,
        // ])
        // ],
        // longitud: ['', Validators.compose([
        // 	Validators.required,
        // ])
        // ],
        telefonoMovil: [
          "",
          Validators.compose([Validators.required])
        ],
        telefonoFijo: [
          "",
          Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])
        ],
        codigoRevendedora: ["", Validators.compose([Validators.minLength(3), Validators.maxLength(100)])],
        divulgacion: [false, Validators.compose([Validators.required])],
        newsletter: [false],
        condiciones: [false, Validators.compose([Validators.required])]
      },
      {
        validator: Validators.compose([
          ConfirmEmailValidator.MatchEmail,
          ConfirmPasswordValidator.MatchPassword,
          FechaNacimientoValidator.ValidAge
        ])
      }
    );
  }

  /**
   * Form Submit
   */
  submit() {
    const controls = this.registerForm.controls;

    if (this.registerForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.authNoticeService.setNotice("El formulario presenta errores", "danger");
      return;
    }

    if (!controls["condiciones"].value) {
      // you must agree the terms and condition
      // checkbox cannot work inside mat-form-field https://github.com/angular/material2/issues/7891
      this.authNoticeService.setNotice("Debes aceptar las condiciones de uso", "danger");
      return;
    }

    let request = {
      lastName: controls["lastName"].value,
      firstName: controls["firstName"].value,
      documentNumber: controls["documentNumber"].value,
      telefonoFijo: controls["telefonoFijo"].value,
      telefonoMovil: controls["telefonoMovil"].value,
      fechaNacimiento: controls["fechaNacimiento"].value,
      sexo: controls["sexo"].value,
      nacionalidad: controls["nacionalidad"].value,
      email: controls["email"].value,
      emailConfirmation: controls["confirmEmail"].value,
      Password: controls["password"].value,
      confirmPassword: controls["confirmPassword"].value,
      domicilios: [
        {
          // 'region': controls['region'].value,
          localidad: controls["partido"].value.n,
          provincia: controls["provincia"].value.n,
          codPostal: controls["codPostal"].value,
          barrio: controls["barrio"].value,
          calle: controls["calle"].value,
          numero: controls["numero"].value,
          piso: controls["piso"].value,
          departamento: controls["departamento"].value,
          complemento: controls["complemento"].value,
          latitud: this.mapsLatitude,
          longitud: this.mapsLongitude,
          type: "Primary"
        }
      ],
      estado: null,
      fecha: new Date(),
      codigoRevendedora: controls["codigoRevendedora"].value,
      newsletter: controls["newsletter"].value
    };

    this.authNoticeService.setNotice(null);
    this.loading = true;
    const httpHeaders = new HttpHeaders();
    httpHeaders.set("Content-Type", "application/json");
    this.http
      .post(API_REGISTER_URL, request, { headers: httpHeaders })
      .subscribe(
        (value) => {
          return this.router.navigateByUrl("/auth/sms/" + value["id"]);
        },
        (error) => {
          if (error.error) {
            let errors = error.error.errors;
            for (let i = 0; i < errors.length; i++) {
              if (errors[i].code === "DuplicateUserName") {
                this.authNoticeService.setNotice(
                  "Ya tienes una cuanta registrada con ese firstName de usuario. Iniciá sesión haciendo click en Atrás.",
                  "danger"
                );
                break;
              } else if (errors[i].code === "DuplicateEmail") {
                this.authNoticeService.setNotice(
                  "Ya tienes una cuanta registrada con ese e-mail. Iniciá sesión haciendo click en Atrás.",
                  "danger"
                );
                break;
              } else if (errors[i].code === "DuplicateDNI") {
                this.authNoticeService.setNotice(
                  "Ya tienes una cuanta registrada con ese DNI. Iniciá sesión haciendo click en Atrás.",
                  "danger"
                );
                break;
              } else if (errors[i].code === "PasswordTooShort") {
                this.authNoticeService.setNotice("La contraseña es muy corta", "danger");
                break;
              } else if (errors[i].code === "PasswordRequiresNonAlphanumeric") {
                this.authNoticeService.setNotice("La contraseña requiere un símbolo", "danger");
                break;
              } else if (errors[i].code === "PasswordRequiresLower") {
                this.authNoticeService.setNotice("La contraseña requiere una letra minúscula", "danger");
                break;
              } else if (errors[i].code === "PasswordRequiresUpper") {
                this.authNoticeService.setNotice("La contraseña requiere una letra mayúscula", "danger");
                break;
              } else if (errors[i].code === "PasswordRequiresDigit") {
                this.authNoticeService.setNotice("La contraseña requiere un número", "danger");
                break;
              }
            }
          } else {
            this.authNoticeService.setNotice("Ha ocurrido un error. Por favor, intenta nuevamente.", "danger");
          }
        }
      )
      .add(() => {
        this.loading = false;
        this.cdr.detectChanges();
      });

    /*
		const _user: User = new User();
		_user.clear();
		_user.email = controls['email'].value;
		_user.username = controls['username'].value;
		_user.fullname = controls['fullname'].value;
		_user.password = controls['password'].value;
		_user.roles = [];
		this.auth.register(_user).pipe(
			tap(user => {
				if (user) {
					this.store.dispatch(new Register({authToken: user.accessToken}));
					// pass notice message to the login page
					this.authNoticeService.setNotice(this.translate.instant('AUTH.REGISTER.SUCCESS'), 'success');
					this.router.navigateByUrl('/auth/login');
				} else {
					this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
				}
			}),
			takeUntil(this.unsubscribe),
			finalize(() => {
				this.loading = false;
				this.cdr.markForCheck();
			})
		).subscribe();
		*/
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to valitors name
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.registerForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  openDialog() {
    const dialogRef = this.dialog.open(DialogResellerSearchComponent, {
      width: "90%"
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.registerForm.patchValue({ codigoRevendedora: result.userName });
      }
    });
  }

  initGoogleMaps() {
    // load Places Autocomplete
    this.mapsAPILoader.load().then(() => {
      this.setCurrentLocation();
      this.geoCoder = new google.maps.Geocoder();

      /*
			let autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
				types: ['address']
			});
			autocomplete.addListener('place_changed', () => {
				this.ngZone.run(() => {
					// get the place result
					let place = autocomplete.getPlace();

					// verify result
					if (place.geometry === undefined || place.geometry === null) {
						return;
					}

					// set latitude, longitude and zoom
					this.mapsLatitude = place.geometry.location.lat();
					this.mapsLongitude = place.geometry.location.lng();
					this.mapsZoom = 15;
					this.cdr.detectChanges();
				});
			});
			*/
    });
  }

  // Get Current Location Coordinates
  private setCurrentLocation() {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.mapsLatitude = position.coords.latitude;
        this.mapsLongitude = position.coords.longitude;
        this.getAddress(this.mapsLatitude, this.mapsLongitude, 12);
      });
    }
  }

  markerDragEnd($event: MouseEvent) {
    this.mapsLatitude = $event.coords.lat;
    this.mapsLongitude = $event.coords.lng;
    this.getAddress(this.mapsLatitude, this.mapsLongitude);
  }

  getAddress(latitude, longitude, zoom = null) {
    this.geoCoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
      if (status === "OK") {
        if (results[0]) {
          if (zoom) {
            this.mapsZoom = zoom;
          }
          this.mapsAddress = results[0].formatted_address;
          this.cdr.detectChanges();
        } else {
          console.log("No results found");
        }
      } else {
        console.log("Geocoder failed due to: " + status);
      }
    });
  }

  updateMapByString() {
    let string =
      this.calle.nativeElement.value +
      " " +
      this.numero.nativeElement.value +
      " " +
      this.registerForm.controls["barrio"].value +
      " " +
      this.registerForm.controls["provincia"].value.n;
    this.setPlaceByString(string);
  }

  heartbeat() {
    const controls = this.registerForm.controls;

    let data = {
      id: null,
      lastName: controls["lastName"].value,
      firstName: controls["firstName"].value,
      documentNumber: controls["documentNumber"].value,
      telefonoFijo: controls["telefonoFijo"].value,
      telefonoMovil: controls["telefonoMovil"].value,
      email: controls["email"].value
    };

    let url;
    if (this.intencion && this.intencion.id) {
      data.id = this.intencion.id;
      url = API_INTENCIONREVENTA_DRAFT_URL;
    } else {
      url = API_INTENCIONREVENTA_START_URL;
    }

    const httpHeaders = new HttpHeaders();
    httpHeaders.set("Content-Type", "application/json");
    this.http.post(url, data, { headers: httpHeaders }).subscribe((value) => {
      this.intencion = value;
    });
  }
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: "dialog-reseller-search",
  templateUrl: "dialog-reseller-search.html"
})
export class DialogResellerSearchComponent implements OnInit {
  searchForm: FormGroup;
  displayedColumns = ["username", "firstName", "lastName", "documentNumber", "select"];
  dataSource = new MatTableDataSource<Element>(ELEMENT_DATA);

  // @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  // Con {static: true} da error. Para qué es?
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /**
   * Set the paginator after the view init since this component will
   * be able to query its view for the initialized paginator.
   */
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private dialogRef: MatDialogRef<DialogResellerSearchComponent>
  ) {}

  ngOnInit() {
    this.initSearchForm();
    this.paginator._intl.itemsPerPageLabel = "Items por página";
  }

  search() {
    const controls = this.searchForm.controls;

    if (this.searchForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    let userSearch = {
      filter: {
        username: controls["username"].value,
        lastName: controls["lastName"].value,
        firstName: controls["firstName"].value,
        documentNumber: controls["documentNumber"].value
      },
      sortOrder: null,
      sortField: null,
      pageNumber: this.paginator.pageIndex + 1,
      pageSize: this.paginator.pageSize
    };

    const httpHeaders = new HttpHeaders();
    httpHeaders.set("Content-Type", "application/json");
    return this.http.post(API_APPLICATIONUSERS_SEARCH_URL, userSearch, { headers: httpHeaders }).subscribe((value) => {
      let items = value["items"];
      this.dataSource = new MatTableDataSource<Element>(items);
    });
  }

  initSearchForm() {
    this.searchForm = this.fb.group(
      {
        username: [
          "",
          Validators.compose([
            // Validators.required,
            // Validators.minLength(3),
            // Validators.maxLength(100)
          ])
        ],
        documentNumber: null,
        lastName: null,
        firstName: null
      },
      {
        validator: Validators.compose([AtLeastOneValidator.atLeastOne])
      }
    );
  }

  clickRow(row) {
    this.dialogRef.close(row);
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.searchForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }
}

export interface Element {
  username: string;
  lastName: number;
  firstName: number;
  documentNumber: string;
}

const ELEMENT_DATA: Element[] = [];
